import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAdminAuthTokens, refreshAdminToken, clearAdminAuthData } from './adminAuthService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.cecurestream.com';
const ADMIN_API_URL = `${API_BASE_URL}/admin`;

class AdminApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: ADMIN_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const tokens = getAdminAuthTokens();
        if (tokens) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newTokens = await refreshAdminToken();
            if (newTokens) {
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            clearAdminAuthData();
            window.location.href = '/admin/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw error.response?.data || error;
      }
      throw error;
    }
  }

  // Customer Earnings APIs
  async getCustomersList(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    minEarnings?: number;
    maxEarnings?: number;
  }) {
    return this.request({
      method: 'GET',
      url: '/customers',
      params,
    });
  }

  async getCustomerDetails(customerId: string) {
    return this.request({
      method: 'GET',
      url: `/customers/${customerId}`,
    });
  }

  async getCustomerEarnings(
    customerId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      groupBy?: string;
    }
  ) {
    return this.request({
      method: 'GET',
      url: `/customers/${customerId}/earnings`,
      params,
    });
  }

  async exportEarnings(params: {
    customerId?: string;
    format: 'csv' | 'json';
    startDate?: string;
    endDate?: string;
  }) {
    const url = params.customerId
      ? `/customers/${params.customerId}/earnings/export`
      : '/earnings/export';

    return this.request({
      method: 'GET',
      url,
      params: {
        format: params.format,
        startDate: params.startDate,
        endDate: params.endDate,
      },
    });
  }

  // Analytics APIs
  async getAnalyticsOverview(period: 'today' | 'week' | 'month' | 'year' | 'all' = 'month') {
    return this.request({
      method: 'GET',
      url: '/analytics/overview',
      params: { period },
    });
  }

  async getTopCustomers(params?: {
    limit?: number;
    metric?: 'earnings' | 'events' | 'attendees';
  }) {
    return this.request({
      method: 'GET',
      url: '/analytics/customers/top',
      params,
    });
  }

  // User Management APIs
  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
  }) {
    return this.request({
      method: 'GET',
      url: '/users',
      params,
    });
  }

  async createAdminUser(userData: {
    email: string;
    role: string;
    permissions?: string[];
    department?: string;
  }) {
    return this.request({
      method: 'POST',
      url: '/users',
      data: userData,
    });
  }

  async updateAdminUserPermissions(userId: string, permissions: string[]) {
    return this.request({
      method: 'PUT',
      url: `/users/${userId}/permissions`,
      data: { permissions },
    });
  }

  // Audit Log APIs
  async getAuditLogs(params?: {
    userId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) {
    return this.request({
      method: 'GET',
      url: '/audit-log',
      params,
    });
  }
}

// Export singleton instance
export const adminApi = new AdminApiService();

// Export types
export interface CustomerListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  joinDate: string;
  totalEarnings: number;
  pendingPayout: number;
  lastPayoutDate?: string;
  totalEvents: number;
}

export interface CustomerDetails {
  customer: {
    id: string;
    email: string;
    name: string;
    joinDate: string;
    subscriptionTier: string;
  };
  earnings: {
    summary: {
      grossRevenue: number;
      platformFees: number;
      stripeFees: number;
      netEarnings: number;
      paidOut: number;
      pendingPayout: number;
    };
    breakdown: {
      byEvent: Array<{
        eventId: string;
        eventName: string;
        grossRevenue: number;
        netRevenue: number;
        paymentCount: number;
      }>;
      byMonth: Array<{
        month: string;
        amount: number;
      }>;
    };
  };
  metrics: {
    totalEvents: number;
    activeEvents: number;
    totalAttendees: number;
    hasStripeAccount: boolean;
  };
}

export interface AnalyticsOverview {
  period: string;
  dateRange: {
    start: string;
    end: string;
  };
  metrics: {
    users: {
      total: number;
      new: number;
      active: number;
      growth: number;
    };
    events: {
      total: number;
      active: number;
      new: number;
      completed: number;
    };
    revenue: {
      gross: number;
      net: number;
      platformFees: number;
      stripeFees: number;
      transactions: number;
      averageTransaction: number;
    };
    attendance: {
      total: number;
      new: number;
      averagePerEvent: number;
    };
    growth: {
      userGrowth: number;
      eventGrowth: number;
      revenueGrowth: number;
    };
  };
}

export interface ExportResponse {
  message: string;
  downloadUrl: string;
  fileName: string;
  recordCount: number;
  expiresIn: number;
}